import { useState } from 'react';
import { X, Users, Building2, Sparkles, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ContioLeadCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  landingPageId?: string;
}

interface FormData {
  name: string;
  email: string;
  role: string;
  teamSize: number;
  useCase: string;
  additionalNotes: string;
}

export default function ContioLeadCaptureModal({ isOpen, onClose, landingPageId = 'contioai' }: ContioLeadCaptureModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    role: '',
    teamSize: 1,
    useCase: '',
    additionalNotes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const useCases = [
    { value: 'investor-calls', label: 'Investor & Board Communications', icon: Users },
    { value: 'customer-support', label: 'Customer Support & Success', icon: Users },
    { value: 'sales-prospecting', label: 'Sales & Prospecting', icon: Building2 },
    { value: 'partnership-outreach', label: 'Partnership & Vendor Calls', icon: Sparkles },
    { value: 'internal-comms', label: 'Cross-Team Coordination', icon: Users },
    { value: 'other', label: 'Other (Tell us more)', icon: Sparkles }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // TODO: API call to save corporate lead
      const response = await fetch('/api/corporate-leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          landing_page_id: landingPageId,
          submitted_at: new Date().toISOString()
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        setTimeout(() => {
          onClose();
          // Reset form after modal closes
          setTimeout(() => {
            setIsSubmitted(false);
            setFormData({
              name: '',
              email: '',
              role: '',
              teamSize: 1,
              useCase: '',
              additionalNotes: ''
            });
          }, 500);
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTeamSizeChange = (value: number) => {
    setFormData(prev => ({ ...prev, teamSize: Math.max(1, Math.min(100, value)) }));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden pointer-events-auto"
            >
              {!isSubmitted ? (
                <>
                  {/* Header */}
                  <div className="relative bg-gradient-to-r from-[#45B787] to-[#3a9970] px-8 py-8 text-white">
                    <button
                      onClick={onClose}
                      className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>

                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold">Join the ContioAI Beta</h2>
                        <p className="text-white/80 text-sm">Early access for Aaron's team</p>
                      </div>
                    </div>

                    <p className="text-white/90 text-sm leading-relaxed">
                      Help us understand how your team would use DoubleMe. We'll custom-build your doubles
                      and give you direct access to our founders for setup and support.
                    </p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6 max-h-[calc(90vh-200px)] overflow-y-auto">
                    {/* Name & Email Row */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Adam Chen"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#45B787] focus:ring-0 transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="adam@contio.ai"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#45B787] focus:ring-0 transition-colors"
                        />
                      </div>
                    </div>

                    {/* Role */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Your Role at ContioAI *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.role}
                        onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                        placeholder="e.g., Engineering Lead, Operations Manager, etc."
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#45B787] focus:ring-0 transition-colors"
                      />
                    </div>

                    {/* Team Size Selector */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        How many people on your team need a double?
                      </label>
                      <div className="flex items-center gap-4">
                        <button
                          type="button"
                          onClick={() => handleTeamSizeChange(formData.teamSize - 1)}
                          className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center font-bold text-xl text-gray-700"
                        >
                          -
                        </button>

                        <div className="flex-1 relative">
                          <div className="text-center">
                            <div className="text-5xl font-bold text-[#45B787] mb-1">
                              {formData.teamSize}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formData.teamSize === 1 ? 'team member' : 'team members'}
                            </div>
                          </div>

                          {/* Visual indicator bar */}
                          <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-[#45B787] to-[#3a9970] transition-all duration-300"
                              style={{ width: `${Math.min(100, (formData.teamSize / 20) * 100)}%` }}
                            />
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleTeamSizeChange(formData.teamSize + 1)}
                          className="w-12 h-12 rounded-full bg-[#45B787] hover:bg-[#3a9970] transition-colors flex items-center justify-center font-bold text-xl text-white"
                        >
                          +
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        We'll work with you to custom-build doubles for your entire team
                      </p>
                    </div>

                    {/* Use Case Selection */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Primary use case *
                      </label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {useCases.map((useCase) => {
                          const Icon = useCase.icon;
                          return (
                            <button
                              key={useCase.value}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, useCase: useCase.value }))}
                              className={`p-4 rounded-xl border-2 transition-all text-left ${
                                formData.useCase === useCase.value
                                  ? 'border-[#45B787] bg-[#45B787]/5 shadow-sm'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <Icon className={`w-5 h-5 mt-0.5 ${
                                  formData.useCase === useCase.value ? 'text-[#45B787]' : 'text-gray-400'
                                }`} />
                                <span className={`text-sm font-medium ${
                                  formData.useCase === useCase.value ? 'text-gray-900' : 'text-gray-600'
                                }`}>
                                  {useCase.label}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Additional Notes */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Tell us more (optional)
                      </label>
                      <textarea
                        value={formData.additionalNotes}
                        onChange={(e) => setFormData(prev => ({ ...prev, additionalNotes: e.target.value }))}
                        placeholder="Any specific workflows, pain points, or questions you'd like us to know about?"
                        rows={3}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#45B787] focus:ring-0 transition-colors resize-none"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting || !formData.name || !formData.email || !formData.role || !formData.useCase}
                      className="w-full py-4 bg-gradient-to-r from-[#45B787] to-[#3a9970] text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5" />
                          Request Early Access
                        </>
                      )}
                    </button>

                    <p className="text-xs text-gray-500 text-center">
                      By submitting, you agree to let us reach out about custom-building your team's doubles.
                    </p>
                  </form>
                </>
              ) : (
                /* Success State */
                <div className="px-8 py-16 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", duration: 0.6 }}
                    className="w-20 h-20 bg-[#45B787] rounded-full flex items-center justify-center mx-auto mb-6"
                  >
                    <CheckCircle className="w-10 h-10 text-white" />
                  </motion.div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    You're on the list!
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-6">
                    We'll reach out within 24 hours to start custom-building your team's doubles.
                    Expect an email from <span className="font-semibold">six@getadouble.com</span>.
                  </p>

                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#45B787]/10 rounded-full text-sm text-[#45B787] font-medium">
                    <Sparkles className="w-4 h-4" />
                    Priority access granted for {formData.teamSize} {formData.teamSize === 1 ? 'person' : 'people'}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
